import type { ConfigState } from "idfweb-runtime-protocol";

/**
 * The set_config tool's text: sdkconfig lines read into the values
 * kconfserver takes, and its reply told back as what changed.
 */

export type ConfigValue = boolean | number | string;

/** A symbol's type, as `kconfig_menus.json` gives it. */
export type SymbolType = "bool" | "int" | "hex" | "string" | "float";

/** How many other changed or shown options a result lists by name. */
const LISTED = 40;

/** One entry of `kconfig_menus.json`, as far as types are concerned. */
interface MenuEntry {
    type: string;
    name: string | null;
    children?: MenuEntry[];
}

/** Each symbol's type, by name without the CONFIG_ prefix. */
export function symbolTypes(menus: readonly MenuEntry[]): Map<string, SymbolType> {
    const types = new Map<string, SymbolType>();
    const walk = (entries: readonly MenuEntry[]) => {
        for (const entry of entries) {
            if (entry.name !== null && !["menu", "choice", "comment"].includes(entry.type)) {
                types.set(entry.name, entry.type as SymbolType);
            }
            walk(entry.children ?? []);
        }
    };
    walk(menus);
    return types;
}

/**
 * The values `text` sets, by name, in the order given; or what is wrong
 * with it. Lines are `CONFIG_NAME=value` or `# CONFIG_NAME is not set`, the
 * prefix optional; empty lines and other comments are skipped.
 */
export function parseSettings(
    text: string,
    types: ReadonlyMap<string, SymbolType>,
): { values: Map<string, ConfigValue> } | { error: string } {
    const values = new Map<string, ConfigValue>();
    const problems: string[] = [];
    for (const raw of text.split(/\r?\n/)) {
        const line = raw.trim();
        const unset = /^#\s*(?:CONFIG_)?(\w+) is not set$/.exec(line);
        if (line === "" || (line.startsWith("#") && unset === null)) continue;
        const assignment = unset === null ? /^(?:CONFIG_)?(\w+)\s*=\s*(.*)$/.exec(line) : null;
        if (unset === null && assignment === null) {
            problems.push(`"${line}" is not a line such as CONFIG_NAME=value.`);
            continue;
        }
        const name = (unset ?? assignment)![1]!;
        const type = types.get(name);
        if (type === undefined) {
            problems.push(`CONFIG_${name} is not an option of this project's configuration that can be set.`);
            continue;
        }
        const value = unset !== null ? typedValue("bool", "n") : typedValue(type, assignment![2]!);
        if (typeof value === "object") problems.push(`CONFIG_${name}: ${value.error}`);
        else values.set(name, value);
    }
    if (problems.length > 0) return { error: `Nothing was changed.\n${problems.join("\n")}` };
    if (values.size === 0) return { error: "The settings hold no CONFIG_NAME=value line." };
    return { values };
}

/** The value kconfserver takes for `literal`, a value as sdkconfig writes it, for a symbol of `type`. */
function typedValue(type: SymbolType, literal: string): ConfigValue | { error: string } {
    const text = literal.trim();
    if (type === "bool") {
        if (/^(y|true)$/i.test(text)) return true;
        if (/^(n|false)$/i.test(text)) return false;
        return { error: `a bool option is y or n, not ${text}.` };
    }
    if (type === "int") {
        return /^-?\d+$/.test(text) ? Number(text) : { error: `an int option takes a whole number, not ${text}.` };
    }
    if (type === "hex") {
        const digits = /^(?:0x)?([0-9a-f]+)$/i.exec(text)?.[1];
        return digits === undefined
            ? { error: `a hex option takes a number such as 0x1000, not ${text}.` }
            : parseInt(digits, 16);
    }
    if (type === "float") {
        return /^-?\d+(\.\d+)?$/.test(text) ? Number(text) : { error: `a float option takes a number, not ${text}.` };
    }
    const quoted = /^"((?:[^"\\]|\\.)*)"$/.exec(text);
    return quoted === null ? text : quoted[1]!.replace(/\\(.)/g, "$1");
}

/** A value as sdkconfig writes it. */
export function formatValue(value: unknown, type: SymbolType | undefined): string {
    if (value === null || value === undefined) return "(no value)";
    if (typeof value === "boolean") return value ? "y" : "n";
    if (typeof value === "number" && type === "hex") return `0x${value.toString(16)}`;
    if (typeof value === "string") return JSON.stringify(value);
    return String(value);
}

/** What a set request changed, told from the state before it and kconfserver's reply. */
export function describeChange(
    requested: ReadonlyMap<string, ConfigValue>,
    types: ReadonlyMap<string, SymbolType>,
    before: ConfigState,
    reply: { values?: Record<string, unknown>; visible?: Record<string, boolean>; error?: string[] },
    sdkconfig: string,
): { text: string; isError: boolean } {
    const after = (name: string) =>
        reply.values !== undefined && name in reply.values ? reply.values[name] : before.values[name];
    const shown = (name: string) => reply.visible?.[name] ?? before.visible[name];
    const set: string[] = [];
    const refused: string[] = [];
    for (const [name, value] of requested) {
        const type = types.get(name);
        const now = after(name);
        const line = `CONFIG_${name}=${formatValue(now, type)}`;
        if (now === value) {
            const was = before.values[name];
            set.push(was === value ? `${line} (it was already)` : `${line} (was ${formatValue(was, type)})`);
        } else if (shown(name) === false) {
            refused.push(`${line}: it is hidden, because an option it depends on is off or a choice excludes it.`);
        } else {
            refused.push(`${line}: the configuration server kept this value.`);
        }
    }
    const parts: string[] = [];
    if (set.length > 0) parts.push(`Set in ${sdkconfig}:\n${set.join("\n")}`);
    if (refused.length > 0) parts.push(`Not set:\n${refused.join("\n")}`);
    if (reply.error !== undefined && reply.error.length > 0)
        parts.push(`The configuration server said:\n${reply.error.join("\n")}`);

    const moved = Object.entries(reply.values ?? {})
        .filter(([name, value]) => types.has(name) && !requested.has(name) && value !== before.values[name])
        .map(
            ([name, value]) =>
                `CONFIG_${name}=${formatValue(value, types.get(name))} (was ${formatValue(before.values[name], types.get(name))})`,
        );
    if (moved.length > 0) parts.push(`Other options that changed with it:\n${listed(moved)}`);
    const visibility = Object.entries(reply.visible ?? {}).filter(
        ([name, visible]) => types.has(name) && visible !== before.visible[name],
    );
    const appeared = visibility.filter(([, visible]) => visible).map(([name]) => `CONFIG_${name}`);
    const hidden = visibility.filter(([, visible]) => !visible).map(([name]) => `CONFIG_${name}`);
    if (appeared.length > 0) parts.push(`Options now shown, which can be set:\n${listed(appeared)}`);
    if (hidden.length > 0) parts.push(`Options now hidden:\n${listed(hidden)}`);
    return { text: parts.join("\n\n"), isError: set.length === 0 };
}

function listed(lines: string[]): string {
    const more = lines.length - LISTED;
    return more > 0 ? `${lines.slice(0, LISTED).join("\n")}\n… and ${more} more` : lines.join("\n");
}
